import React, { useMemo } from "react";

import { useIntl, FormattedMessage } from "react-intl";
import { useNavigate } from "react-router-dom";

import Button from "ds/Button";
import Loading from "ds/PageLoading";
import * as Sticker from "ds/Sticker";
import useDecodedParams from "Hooks/useDecodedParams";
import useSelectorWithUrlParams from "Hooks/useSelectorWithUrlParams";
import { isDg2 } from "Libs/utils";
import { meSelector } from "Reducers/app/selectors";
import { environmentSelector } from "Reducers/environment";
import { selectHasEnterpriseDeployment } from "Reducers/environment/environment.selectors";
import { gitProjectSelector } from "Reducers/project/project";
import { useLoadDomains } from "Reducers/project/settings/domains/hooks/useLoadDomains";
import {
  subscriptionDedicatedSelector,
  subscriptionSelector
} from "Reducers/subscription";
import ConfigureDomainInfo from "src/organization/pages/project/environment/settings/domains/components/ConfigureDomainInfo/ConfigureDomainInfo";
import {
  determineUserState,
  getStickerText
} from "src/organization/pages/project/environment/settings/domains/utils";
import { useAppSelector } from "Store/hooks";

import * as S from "./styles";

const EnvironmentDomainsEmpty = ({
  capabilityCustomDomains,
  userHasPermissions
}) => {
  const { environmentId, organizationId, projectId } = useDecodedParams<{
    environmentId: string;
    organizationId: string;
    projectId: string;
  }>();

  const environment = useSelectorWithUrlParams(environmentSelector);

  //const [currentUser, setCurrentUser] = useState<string | UserTypeType>("");

  const intl = useIntl();

  const navigate = useNavigate();

  const encodedEnvironmentId = encodeURIComponent(environmentId);

  const project = useSelectorWithUrlParams(gitProjectSelector);

  const isDevelopment = useMemo(
    () => project?.subscription?.plan === "development",
    [project]
  );

  const { domains, loading } = useLoadDomains(project);

  const {
    isProductionEnvironment,
    hasCustomDomainsOrProUser,
    noProductionPlan,
    noProductionDomain,
    hasDomainsProdEnvNoProdPlan
  } = determineUserState(
    environment,
    isDevelopment,
    domains,
    capabilityCustomDomains,
    userHasPermissions
  );

  const addDomain = () => {
    navigate(
      isProductionEnvironment
        ? `/${organizationId}/${projectId}/-/settings/domains/-/new`
        : `/${organizationId}/${projectId}/${encodedEnvironmentId}/settings/domains/-/add`
    );
  };

  const enterpriseDeployment = useSelectorWithUrlParams(
    selectHasEnterpriseDeployment
  );

  const subscription = useSelectorWithUrlParams(subscriptionSelector, {
    id: project?.subscription_id
  });

  const isDedicated = useSelectorWithUrlParams(subscriptionDedicatedSelector, {
    id: project?.subscription_id
  });

  const isDg2Project = isDg2(
    subscription?.enterprise_tag,
    enterpriseDeployment
  );

  const user = useAppSelector(meSelector);

  const onClickStickerButton = () => {
    if (noProductionPlan) {
      const url = isDedicated
        ? `/-/users/${user?.username}/tickets/open`
        : `/${organizationId}/-/billing/plan/${project?.getSubscriptionId()}`;
      return navigate(url);
    }

    navigate(`/${organizationId}/${projectId}/-/settings/domains/-/new`);
  };

  return loading ? (
    <Loading />
  ) : (
    <S.MainContainer>
      {hasDomainsProdEnvNoProdPlan && userHasPermissions && (
        <ConfigureDomainInfo />
      )}
      {(!hasCustomDomainsOrProUser ||
        !userHasPermissions ||
        noProductionPlan) &&
        (capabilityCustomDomains || isProductionEnvironment) && (
          <S.StickerRoot priority={isDg2Project ? "normal" : "information"}>
            <Sticker.Icon priority={isDg2Project ? "normal" : "information"} />
            <Sticker.Text textStyle="information">
              {intl.formatMessage({
                id: getStickerText({
                  isDg2Project,
                  noProductionPlan,
                  noProductionDomain,
                  isProductionEnvironment
                })
              })}
            </Sticker.Text>
            {(noProductionPlan || noProductionDomain) && (
              <Button
                onClick={onClickStickerButton}
                analyticId={
                  noProductionPlan
                    ? "upgrade"
                    : "settings.domains.button.add_production_domain"
                }
              >
                {noProductionPlan && !isDedicated
                  ? intl.formatMessage({ id: "upgrade" })
                  : noProductionPlan && isDedicated
                    ? intl.formatMessage({
                        id: "settings.domains.button.dedicated"
                      })
                    : intl.formatMessage({
                        id: "settings.domains.button.add_production_domain"
                      })}
              </Button>
            )}
          </S.StickerRoot>
        )}

      {
        <S.EmptyDomainsWrapper>
          <S.Title tag="h2">
            {!capabilityCustomDomains && !isProductionEnvironment
              ? intl.formatMessage({
                  id: "settings.domains.empty.upgrade.title"
                })
              : isProductionEnvironment
                ? intl.formatMessage({ id: "settings.domains.add.new.title" })
                : intl.formatMessage({
                    id: "settings.domains.custom.add.new.title"
                  })}
          </S.Title>
          <S.SubTitle>
            {!capabilityCustomDomains && !isProductionEnvironment ? (
              intl.formatMessage({
                id: "settings.domains.empty.upgrade.body"
              })
            ) : (
              <FormattedMessage
                id={
                  isProductionEnvironment
                    ? "settings.domains.add.body"
                    : "settings.domains.add.staging.body"
                }
                values={{
                  i: txt => <i>{txt}</i>
                }}
              />
            )}
          </S.SubTitle>

          {!capabilityCustomDomains && !isProductionEnvironment ? (
            <>
              <a
                href={intl.formatMessage({ id: "links.contact" })}
                target="_blank"
                rel="noreferrer"
                data-pendo-identifier="links.contact"
              >
                <S.AddButton
                  analyticId="support_sla_link_text_contact"
                  id="add-new-custom-domain"
                  disabled={!hasCustomDomainsOrProUser}
                >
                  {intl.formatMessage({ id: "support_sla_link_text_contact" })}
                </S.AddButton>
              </a>
              <S.ContactSupport>
                {intl.formatMessage<React.ReactNode>(
                  { id: "settings.domains.contact_support" },
                  {
                    link: chunks => (
                      <S.Link
                        href={intl.formatMessage({
                          id: "links.platform.contact"
                        })}
                        analyticId="links.platform.contact"
                      >
                        {chunks}
                      </S.Link>
                    )
                  }
                )}
              </S.ContactSupport>
            </>
          ) : (
            <S.AddButton
              id="add-new-custom-domain"
              onClick={addDomain}
              disabled={
                !hasCustomDomainsOrProUser ||
                !userHasPermissions ||
                noProductionPlan
              }
              analyticId="settings.domains.add.new.title"
            >
              {intl.formatMessage({ id: "settings.domains.add.new.title" })}
            </S.AddButton>
          )}
        </S.EmptyDomainsWrapper>
      }
    </S.MainContainer>
  );
};
export default EnvironmentDomainsEmpty;
